import { Injectable } from '@angular/core';
import { AuthConfig, NullValidationHandler, OAuthService } from 'angular-oauth2-oidc';
import { environment } from '../../environments/environment';

export type B2cKind = 'sol' | 'scs';

/**
 * Two B2C policies share the one OAuthService: the normal SCS policy (azureAdConfig) and Sanlam
 * Online's federated policy (solAzureAdConfig). Which one an entry uses is decided in this order:
 *   1. the platform of an existing session (token claim)
 *   2. utm_campaign is one of environment.solUtmCampaigns (e.g. g, legacy) -> SOL
 *   3. default: SCS
 * /scs-consent is always SOL.
 */
@Injectable({
  providedIn: 'root'
})
export class B2cConfigService {
  private readonly consentPath = '/scs-consent';
  private readonly resolvedKey = 'scsResolvedPlatform';
  /** Held in this tab's memory: another tab logging out clears shared storage but not this. */
  private appliedKind: B2cKind | null = null;

  /** Pick used at startup and whenever a sign-in is about to start. */
  initialKind(): B2cKind {
    if (globalThis.location.pathname.toLowerCase().startsWith(this.consentPath)) {
      return 'sol';
    }
    return this.knownKind() ?? this.campaignKind() ?? 'scs';
  }

  configFor(kind: B2cKind) {
    return kind === 'sol' ? environment.solAzureAdConfig : environment.azureAdConfig;
  }

  /** The policy this tab is currently configured for, regardless of what is in storage. */
  currentKind(): B2cKind | null {
    return this.appliedKind;
  }

  apply(oauthService: OAuthService, kind: B2cKind = this.initialKind()): void {
    this.appliedKind = kind;
    const config = this.configFor(kind);
    oauthService.configure(config.authConfig as AuthConfig);
    oauthService.tokenValidationHandler = new NullValidationHandler();
    oauthService.loadDiscoveryDocument(config.discoveryDocumentConfig.url);
    oauthService.setStorage(localStorage);
  }

  /**
   * Called once the sign-in state is known: for a new entry from a SOL campaign, switches the
   * OAuthService to the SOL policy. Everyone else stays on SCS.
   */
  refreshForEntry(oauthService: OAuthService): void {
    if (!this.knownKind() && this.campaignKind() === 'sol') {
      this.apply(oauthService, 'sol');
    }
  }

  /** Hosts of both policies, so the HTTP interceptor treats either as the identity provider. */
  identityHosts(): string[] {
    return [environment.azureAdConfig, environment.solAzureAdConfig]
      .map(config => {
        try {
          return new URL(config.authConfig?.loginUrl || '').hostname;
        } catch {
          return '';
        }
      })
      .filter(host => !!host);
  }

  /** The platform of the current signed-in session, if any. */
  private knownKind(): B2cKind | null {
    return this.flaggedPlatform(this.claims());
  }

  /**
   * SOL when utm_campaign is on the environment's list. The UTM is only on the landing URL, so
   * the match is kept for the rest of the browser session.
   */
  private campaignKind(): B2cKind | null {
    const campaigns = (environment.solUtmCampaigns || []).map(value => value.trim().toLowerCase());
    const fromUrl = new URLSearchParams(globalThis.location.search);
    let utmCampaign = '';
    fromUrl.forEach((value, key) => {
      if (key.toLowerCase() === 'utm_campaign') {
        utmCampaign = value.trim().toLowerCase();
      }
    });
    try {
      if (utmCampaign && campaigns.includes(utmCampaign)) {
        sessionStorage.setItem(this.resolvedKey, 'sol');
        return 'sol';
      }
      return sessionStorage.getItem(this.resolvedKey) === 'sol' ? 'sol' : null;
    } catch {
      return campaigns.includes(utmCampaign) ? 'sol' : null;
    }
  }

  /**
   * extension_TargetPlatform is the authoritative flag. Until the B2C policies emit it, a SOL
   * token is recognised by extension_sanlam_guid; no claim at all means not signed in.
   */
  private flaggedPlatform(claims: any): B2cKind | null {
    const target = String(claims.extension_TargetPlatform ?? '').toLowerCase();
    if (target === 'sol' || target === 'scs') {
      return target;
    }
    if (claims.extension_sanlam_guid) {
      return 'sol';
    }
    return Object.keys(claims).length ? 'scs' : null;
  }

  private claims(): any {
    try {
      return JSON.parse(localStorage.getItem('id_token_claims_obj') || '{}') || {};
    } catch {
      return {};
    }
  }
}
